import { IconHome, IconProfile, IconQueue, IconSearch } from '@akpl/ui-mobile';
import {
  Link,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { type FormEvent, lazy, Suspense, useEffect, useState } from 'react';

import { useAuth } from '../../auth';
import { colorFor, cx } from '../../ui';
import fiestaIcon from '../assets/fiesta.svg';
import markImage from '../assets/mark.png';
import searchIcon from '../assets/search.svg';
import wordmarkImage from '../assets/wordmark.png';
import { useViewer, useWebT } from '../hooks';
import { useCompanion, useWebSession, WebSessionProvider } from '../session/WebSessionProvider';
import { useWebUi } from '../store';
import { AccountLifecycle } from './AccountGuard';
import { AccountMenu } from './AccountMenu';
import { CheckoutHost } from './CheckoutHost';
import { PartyBanner } from './PartyBanner';
import { WButton } from './primitives';
import { SearchSuggestions } from './SearchSuggestions';

const AuthDialog = lazy(() =>
  import('../modals/AuthDialogs').then((m) => ({ default: m.AuthDialog })),
);
const SongDialog = lazy(() =>
  import('../modals/SongDialog').then((m) => ({ default: m.SongDialog })),
);
const Paywall = lazy(() => import('../modals/Paywall').then((m) => ({ default: m.Paywall })));

function Brand({ compact = false }: { compact?: boolean }) {
  const t = useWebT();
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label={t('web:common.brand')}>
      <img
        src={markImage}
        alt=""
        className={compact ? 'size-7 object-contain' : 'size-[30px] object-contain'}
      />
      <img
        src={wordmarkImage}
        alt=""
        className={
          compact
            ? // Narrow phones: the mark alone leaves room for Fiesta, Premium and the avatar.
              'h-[18px] w-[104px] object-contain max-[380px]:hidden'
            : 'h-[22px] w-[124px] object-contain'
        }
      />
    </Link>
  );
}

function SearchBox({ placeholder, className }: { placeholder: string; className?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = useSearch({ strict: false }) as { q?: string };
  const [value, setValue] = useState(current.q ?? '');
  const [suggesting, setSuggesting] = useState(false);

  // Adjusted during render, so a back navigation never paints the previous query for a frame.
  const urlQuery = location.pathname === '/buscar' ? (current.q ?? '') : null;
  const [syncedQuery, setSyncedQuery] = useState(urlQuery);
  if (urlQuery !== syncedQuery) {
    setSyncedQuery(urlQuery);
    if (urlQuery !== null) setValue(urlQuery);
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSuggesting(false);
    const q = value.trim();
    void navigate({ to: '/buscar', search: q ? { q } : {} });
  };

  return (
    <form
      role="search"
      onSubmit={submit}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setSuggesting(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setSuggesting(false);
      }}
      className={cx('relative flex min-w-0 items-center gap-2.5 bg-surface-2', className)}
    >
      <img src={searchIcon} alt="" width={18} height={18} className="shrink-0" />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onFocus={() => setSuggesting(true)}
        onChange={(event) => {
          const next = event.target.value;
          setValue(next);
          setSuggesting(true);
          // Results follow the typing once the user is on the results page.
          if (location.pathname === '/buscar') {
            void navigate({
              to: '/buscar',
              search: next.trim() ? { q: next } : {},
              replace: true,
            });
          }
        }}
        className="min-w-0 flex-1 border-0 bg-transparent text-ink outline-none placeholder:text-muted"
      />
      {/* E-28. On `/buscar` the results already follow the typing, so nothing to suggest. */}
      {suggesting && location.pathname !== '/buscar' ? (
        <SearchSuggestions query={value} onClose={() => setSuggesting(false)} />
      ) : null}
    </form>
  );
}

function Avatar({ onClick }: { onClick?: () => void }) {
  const t = useWebT();
  const auth = useAuth();
  const seed = auth.user?.uid ?? 'a';
  const label = auth.user?.displayName ?? auth.user?.email ?? '?';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('web:common.nav.account')}
      data-account-avatar
      className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-[18px] font-bold text-on-accent"
      style={{ backgroundColor: colorFor(seed) }}
    >
      {auth.user?.photoUrl ? (
        <img src={auth.user.photoUrl} alt="" className="size-full object-cover" />
      ) : (
        label.charAt(0).toUpperCase()
      )}
    </button>
  );
}

const NAV_LINK = 'text-[15px] font-medium text-muted hover:text-ink';
const NAV_LINK_ACTIVE = { className: 'font-semibold !text-ink' };

/** W-11 to W-15 and MW-11 keep "Inicio" lit: these pages hang from the Home, not from a tab of theirs. */
function isHomeSection(pathname: string): boolean {
  return pathname === '/' || /^\/(cancion|lista|gratis|artista|planes)(\/|$)/.test(pathname);
}

/** W/Nav 111:85, states Visitante, Miembro, Fiesta and Premium. */
export function DesktopNav() {
  const t = useWebT();
  const viewer = useViewer();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const session = useWebSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const inParty = session.mode === 'party' && !session.ended; // E-24: the selector is back on Solo.
  const member = viewer.tier !== 'visitor';
  const homeActive = isHomeSection(useLocation().pathname);

  return (
    <header className="sticky top-0 z-30 hidden h-[72px] items-center gap-8 border-b border-surface-2 bg-bg px-12 lg:flex">
      <Brand />
      <nav className="flex shrink-0 items-center gap-6">
        <Link
          to="/"
          aria-current={homeActive ? 'page' : undefined}
          className={cx(NAV_LINK, homeActive && NAV_LINK_ACTIVE.className)}
        >
          {t('web:common.nav.home')}
        </Link>
        <Link to="/buscar" className={NAV_LINK} activeProps={NAV_LINK_ACTIVE}>
          {t('web:common.nav.search')}
        </Link>
        <Link to="/mi-musica" className={NAV_LINK} activeProps={NAV_LINK_ACTIVE}>
          {t('web:common.nav.myMusic')}
        </Link>
      </nav>
      <SearchBox
        placeholder={t('web:common.nav.searchPlaceholder')}
        className="h-11 flex-1 rounded-[22px] px-4 text-[14px]"
      />
      <div className="relative flex shrink-0 items-center gap-3">
        {member ? (
          <>
            <div
              role="group"
              className="flex gap-0.5 rounded-[10px] bg-surface-2 p-[3px] text-[13px] font-semibold"
            >
              <button
                type="button"
                aria-pressed={!inParty}
                onClick={() => void session.setMode('solo')}
                className={cx(
                  'rounded-[8px] px-3.5 py-[7px]',
                  inParty ? 'text-muted' : 'bg-surface-4 text-ink',
                )}
              >
                {t('web:common.nav.solo')}
              </button>
              <button
                type="button"
                aria-pressed={inParty}
                onClick={() => void session.setMode('party')}
                className={cx(
                  'rounded-[8px] px-3.5 py-[7px]',
                  inParty ? 'bg-pink text-white' : 'text-muted',
                )}
              >
                {inParty
                  ? t('web:common.nav.partyActive', {
                      code: session.joinCode ?? '',
                      count: session.memberCount,
                    })
                  : t('web:common.nav.party')}
              </button>
            </div>
            {viewer.tier === 'premium' ? (
              <Link
                to="/cuenta"
                className="rounded-[10px] bg-surface-2 px-[18px] py-[11px] text-[14px] font-semibold text-cyan"
              >
                {t('web:common.nav.premium')}
              </Link>
            ) : (
              <Link
                to="/planes"
                className="rounded-[10px] border-[1.5px] border-pink px-[18px] py-[11px] text-[14px] font-semibold text-pink"
              >
                {t('web:common.nav.upgrade')}
              </Link>
            )}
            <Avatar onClick={() => setMenuOpen((open) => !open)} />
            {menuOpen ? <AccountMenu onClose={() => setMenuOpen(false)} /> : null}
          </>
        ) : (
          <>
            <WButton variant="ghost" size="md" onClick={() => openOverlay({ kind: 'login' })}>
              {t('web:common.nav.login')}
            </WButton>
            <WButton size="md" onClick={() => openOverlay({ kind: 'signup' })}>
              {t('web:common.nav.signup')}
            </WButton>
          </>
        )}
      </div>
    </header>
  );
}

/**
 * Header of MW-01. Standalone: Fiesta, Premium and the avatar. Companion: the pink strip takes
 * over (`PartyBanner`), and this phone never shows a party QR (§5.4).
 */
export function MobileHeader() {
  const t = useWebT();
  const viewer = useViewer();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const companion = useCompanion();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // MW-06, MW-12 and ME-08 start right under the status bar: "Perfil" in the tab bar is the way here.
  if (pathname.startsWith('/mi-musica') || pathname.startsWith('/cuenta')) return null;
  // MW-04, MW-09, MW-10, MW-11, ME-07 and ME-14 as well: on these, the page itself comes first.
  if (/^\/(buscar|lista|gratis|artista|cancion|planes)(\/|$)/.test(pathname)) return null;
  return (
    <header
      data-phone-chrome
      className="flex items-center gap-1.5 px-4 pt-[calc(var(--akm-safe-top)+8px)] pb-2 lg:hidden"
    >
      <Brand compact />
      <span className="flex-1" />
      {companion.active ? null : (
        <Link
          to="/fiesta/unirse"
          // 2 px less per side than MW-01, whose avatar runs off the frame: here it has to fit in 390.
          className="flex shrink-0 items-center gap-1.5 rounded-[16px] bg-surface-2 px-2 py-[7px] text-[13px] font-semibold text-ink"
        >
          <img src={fiestaIcon} alt="" width={14} height={14} />
          {t('web:common.nav.party')}
        </Link>
      )}
      {viewer.tier === 'premium' ? (
        <span className="text-[13px] font-semibold text-cyan">{t('web:common.nav.premium')}</span>
      ) : viewer.tier === 'free' ? (
        <Link
          to="/planes"
          className="rounded-[16px] border-[1.5px] border-pink px-3 py-[7px] text-[13px] font-semibold text-pink"
        >
          {t('web:common.nav.premiumShort')}
        </Link>
      ) : (
        <WButton
          size="sm"
          className="!rounded-[16px] !px-3 !py-[7px] !text-[13px]"
          onClick={() => openOverlay({ kind: 'login' })}
        >
          {t('web:common.nav.login')}
        </WButton>
      )}
      {viewer.isAccount ? <Avatar onClick={() => void navigate({ to: '/cuenta' })} /> : null}
    </header>
  );
}

/** M/Tab bar 46:250. Fila only exists while this phone is in a party. */
export function MobileTabBar() {
  const t = useWebT();
  const companion = useCompanion();
  const location = useLocation();
  // MW-04, MW-09 and ME-14: someone arriving from outside, with the whole screen for the song or plan.
  if (!companion.active && /^\/(cancion|planes)(\/|$)/.test(location.pathname)) return null;
  const tabs = [
    { to: '/', label: t('web:common.tabs.home'), icon: IconHome, exact: true },
    { to: '/buscar', label: t('web:common.tabs.search'), icon: IconSearch },
    // MW-06 and ME-08 light "Perfil" too: Mi música hangs from it on a phone (MW-12).
    { to: '/cuenta', label: t('web:common.tabs.profile'), icon: IconProfile, also: '/mi-musica' },
    ...(companion.active
      ? [
          {
            to: `/j/${companion.joinCode ?? ''}`,
            label: t('web:common.tabs.queue'),
            icon: IconQueue,
            badge: companion.live.items.filter((item) => item.status === 'queued').length,
          },
        ]
      : []),
  ];
  return (
    <nav
      data-phone-chrome
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-surface-2 bg-surface px-2 pt-2.5 pb-[calc(var(--akm-safe-bottom)+10px)] lg:hidden"
    >
      {tabs.map((tab) => {
        const active = tab.exact
          ? isHomeSection(location.pathname)
          : location.pathname.startsWith(tab.to) ||
            (tab.also !== undefined && location.pathname.startsWith(tab.also));
        const Icon = tab.icon;
        return (
          <Link
            key={tab.to}
            to={tab.to as never}
            aria-current={active ? 'page' : undefined}
            className={cx(
              'relative flex flex-1 flex-col items-center gap-1 text-[12px] font-semibold',
              active ? 'text-cyan' : 'text-muted',
            )}
          >
            <Icon size={24} />
            {tab.label}
            {tab.badge ? (
              <span className="absolute top-[-4px] left-1/2 ml-1.5 rounded-[9px] bg-pink px-1.5 py-px text-[11px] font-bold text-ink">
                {tab.badge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

function Footer() {
  const t = useWebT();
  const link = 'text-muted hover:text-ink';
  return (
    <footer className="hidden items-center gap-6 bg-surface px-12 py-7 text-[13px] lg:flex">
      <span className="text-muted">
        {t('web:common.footer.copyright', { year: new Date().getFullYear() })}
      </span>
      <Link to="/planes" className={link}>
        {t('web:common.footer.plans')}
      </Link>
      <span className="text-muted">{t('web:common.footer.help')}</span>
      <span className="text-muted">{t('web:common.footer.terms')}</span>
      <span className="text-muted">{t('web:common.footer.privacy')}</span>
      {/* `/tv` is the pairing flow of the stick, unchanged. */}
      <a href="/tv" className="font-semibold text-cyan">
        {t('web:common.footer.tvStick')}
      </a>
    </footer>
  );
}

function OverlayHost() {
  const overlay = useWebUi((s) => s.overlay);
  if (!overlay) return null;
  return (
    <Suspense fallback={null}>
      {overlay.kind === 'song' ? (
        <SongDialog song={overlay.song} />
      ) : overlay.kind === 'paywall' ? (
        <Paywall {...(overlay.song ? { song: overlay.song } : {})} />
      ) : (
        <AuthDialog overlay={overlay} />
      )}
    </Suspense>
  );
}

function WebToasts() {
  const toasts = useWebUi((s) => s.toasts);
  const dismiss = useWebUi((s) => s.dismissToast);
  // E-31: over the player they go up, on the video, where they do not cover the controls.
  const stageUp = useWebUi((s) => s.stageUp);
  useEffect(() => {
    const timers = toasts.map((toast) => setTimeout(() => dismiss(toast.id), toast.ttlMs));
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);
  if (toasts.length === 0) return null;
  return (
    <div
      className={cx(
        'pointer-events-none fixed inset-x-4 bottom-24 z-50 flex flex-col items-center gap-2 lg:bottom-8',
        stageUp && 'lg:top-[90px] lg:right-[416px] lg:bottom-auto',
      )}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          data-testid="web-toast"
          role={toast.tone === 'error' ? 'alert' : 'status'}
          className={cx(
            // "toast" 126:1916; over the player, only its gap changes.
            'pointer-events-auto flex max-w-[480px] items-center rounded-[14px] bg-surface-4 px-5 py-3.5 text-[14px] leading-[normal] font-medium text-ink',
            stageUp ? 'gap-[18px]' : 'gap-5',
          )}
        >
          <span className="min-w-0 flex-1">{toast.message}</span>
          {toast.action ? (
            <button
              type="button"
              className="font-bold text-cyan"
              onClick={() => {
                toast.action?.run();
                dismiss(toast.id);
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** The frame of every route in §4. */
export function WebLayout() {
  const matches = useMatches();
  const staticData = matches.at(-1)?.staticData;
  const noChrome = staticData?.chrome === 'none';
  return (
    <WebSessionProvider>
      {/*
       * `leading-[normal]`: the frames use Inter's own line height (a 30 px title is 36 px tall), and
       * the 1.35 of `ui-mobile` belongs to `/guest` and `/app`. `:has`: a full-page state (E-07, E-11,
       * E-14) ends at the content on a route whose other frames do have a footer.
       */}
      <div
        data-web
        className="flex min-h-dvh flex-col bg-bg leading-[normal] has-[[data-no-footer]]:[&>footer]:hidden has-[[data-no-phone-chrome]]:max-lg:[&>[data-phone-chrome]]:hidden"
      >
        {noChrome ? null : (
          <>
            <PartyBanner />
            <DesktopNav />
            {staticData?.mobileHeader === false ? null : <MobileHeader />}
          </>
        )}
        <main className={cx('flex min-w-0 flex-1 flex-col', !noChrome && 'pb-24 lg:pb-0')}>
          <Outlet />
        </main>
        {noChrome || staticData?.footer === false ? null : <Footer />}
        {noChrome ? null : <MobileTabBar />}
      </div>
      <OverlayHost />
      <WebToasts />
      <CheckoutHost />
      <AccountLifecycle />
    </WebSessionProvider>
  );
}
